import React, { useState, useEffect, useContext, useMemo } from "react";
import {
    PDFDownloadLink,
    Document,
    Page,
    Text,
    View,
    Image,
    StyleSheet,
    Font,
} from "@react-pdf/renderer";
import CenturyGothicFont from "Assets/fonts/centurygothic.ttf";
import CenturyGothicFontBold from "Assets/fonts/centurygothic_bold.ttf";
import { renderHtmlToReactPdf } from "./utils/RenderHtmlToReactPdf";
import { minimizeHtmlSpacing, removeHeaderImageBlock, removePreImageContent, removePreImageContents } from "./utils/MinimizeHtmlSpacing";
import { JobDataContext } from "State/JobsDataContext";
import { marked } from "marked";

Font.register({
    family: 'CenturyGothic',
    fonts: [
        {
            src: CenturyGothicFont,
            fontWeight: 'normal',
        },
        {
            src: CenturyGothicFontBold,
            fontWeight: 'bold',
        },
        {
            src: CenturyGothicFont,
            fontStyle: 'italic',
        },
    ],
});


function extractFirstImageUrl(htmlString) {
    const match = htmlString.match(
        /<img\s+(?:[^>]*?\s+)?src\s*=\s*(["'])(.*?)\1/i
    );
    return match ? match[2] : null;
}


/**
 * Component to download PDF from Markdown content
 * Converts markdown to HTML first, then generates PDF
 */
const SbDownloadMarkdownPdf = ({ markdownContent }) => {
    const { selectedJob } = useContext(JobDataContext);
    const jobData = selectedJob;

    const fileName = `${jobData?.sb_requisition_number || "Position#"}_${jobData?.job_title || "CompanySpecificRoleTitle"}_V${jobData?.version || "Version#"}.pdf`;


    // Convert markdown to HTML
    const htmlContent = useMemo(() => {
        if (!markdownContent) return "";
        try {
            return marked(markdownContent);
        } catch (error) {
            console.error("Error converting markdown to HTML:", error);
            return markdownContent;
        }
    }, [markdownContent]);

    const firstImageUrl = htmlContent ? extractFirstImageUrl(htmlContent) : null;


    const styles = StyleSheet.create({
        page: {
            flexDirection: 'column',
            padding: 30,
            fontFamily: 'CenturyGothic',
            fontSize: 10,
            lineHeight: 1.4,
            position: 'relative',
        },
        content: {},
        text: {
            fontFamily: 'CenturyGothic',
            fontSize: 10,
            lineHeight: 1.4
        },
        watermark: {
            position: 'absolute',
            top: '40%',
            left: 0,
            right: 0,
            textAlign: 'center',
            fontSize: 100,
            color: 'grey',
            opacity: 0.2,
            transform: 'rotate(-45deg)',
            zIndex: -1,
        },
        section: {
            marginTop: 10,
            marginBottom: 0,
        },
        disclaimerHeading: {
            fontSize: 13,
            fontWeight: 'bold',
            fontFamily: 'CenturyGothic',
            marginBottom: 4,
        },
        disclaimer: {
            marginTop: 3,
            marginBottom: 1,
            fontFamily: 'CenturyGothic',
            color: '#2B3034',
            fontSize: 11,
            lineHeight: 1.2,
        },
    });

    const minimizedHtmlContent = minimizeHtmlSpacing(htmlContent);
    // const cleanedHtmlContent = removePreImageContents(minimizedHtmlContent, firstImageUrl);
    const cleanedHtmlContent = removeHeaderImageBlock(minimizedHtmlContent);

    const parsedContent = new DOMParser().parseFromString(`<html><body>${cleanedHtmlContent}</body></html>`, 'text/html');
    const parsedReactComponents = Array.from(parsedContent.body.children)
        .filter(node => node.tagName !== "IMG") // 🚀 IMPORTANT
        .map((node, index) => (
            <React.Fragment key={index}>
                {renderHtmlToReactPdf(node, styles.text)}
            </React.Fragment>
        ));


    const renderDisclaimer = () => (
        <View style={styles.section}>
            <Text style={styles.disclaimerHeading}>Disclaimer:</Text>
            <Text style={styles.disclaimer}>
                This job description is provided for illustrative and demonstration purposes only. It was generated using artificial intelligence technology. As an AI-generated document, it may not fully reflect current industry standards, specific organizational needs, or human expertise.
            </Text>
            <Text style={styles.disclaimer}>
                Users are encouraged to independently verify the accuracy and applicability of this job description to their specific situation before relying on it for any decisions.
            </Text>
            <Text style={styles.disclaimer}>
                SkillsBridge.AI disclaims all liability for any errors, omissions, inaccuracies, or potential biases in this document. Any reliance you place on such information is strictly at your own risk. Unauthorized use, disclosure, or distribution is strictly prohibited.
            </Text>
        </View>
    );

    const SbJdDocument = () => (
        <Document>
            <Page style={styles.page}>
                <Text style={styles.watermark} fixed>DRAFT</Text>
                <View style={styles.content}>
                    {firstImageUrl && (
                        <Image
                            src={firstImageUrl}
                            style={{ width: "100%", marginBottom: 12 }}
                        />
                    )}
                    {parsedReactComponents}
                    {renderDisclaimer()}
                </View>
            </Page>
        </Document>
    );

    return (
        <PDFDownloadLink
            document={<SbJdDocument />}
            fileName={fileName}
        >
            <svg
                cursor="pointer"
                width="30"
                height="30"
                viewBox="0 0 30 30"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
            >
                <rect width="30" height="30" rx="3" fill="white" />
                <path
                    d="M6.5 15.8889V21.2222C6.5 21.6937 6.72388 22.1459 7.1224 22.4793C7.52091 22.8127 8.06141 23 8.625 23H21.375C21.9386 23 22.4791 22.8127 22.8776 22.4793C23.2761 22.1459 23.5 21.6937 23.5 21.2222V15.8889M15 7V17.6667M15 17.6667L11.2812 14.5556M15 17.6667L18.7188 14.5556"
                    stroke="#8790A4"
                    strokeWidth="1.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                />
            </svg>
        </PDFDownloadLink>
    );
};

export default SbDownloadMarkdownPdf;
